import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../utils/config';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, RefreshCw, Download, ChevronDown, ChevronRight, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABELS = {
  ok: { text: '✅ Inviato', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  partial: { text: '⚠️ Parziale', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  all_failed: { text: '❌ Tutti errori', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
  pending: { text: '⏳ Da inviare', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  no_movements: { text: '➖ Nessun movimento', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  no_delegation: { text: '🚫 No delega', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
};

/**
 * Pannello stato scheduler automatico ROSS1000.
 */
const SchedulerStatusCard = () => {
  const [status, setStatus] = useState(null);
  const [triggering, setTriggering] = useState(false);

  const loadStatus = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const res = await axios.get(`${API}/api/istat/scheduler/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const triggerNow = async () => {
    if (!window.confirm("Eseguire ORA il job daily? Invierà i movimenti di OGGI per tutti gli immobili configurati.")) return;
    setTriggering(true);
    try {
      const token = localStorage.getItem('hw_token');
      const res = await axios.post(
        `${API}/api/istat/scheduler/trigger-daily`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Job avviato: ${res.data.job_id?.slice(0, 8)}…`);
      setTimeout(loadStatus, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore avvio');
    } finally {
      setTriggering(false);
    }
  };

  if (!status) return null;

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('it-IT');
    } catch {
      return iso;
    }
  };

  const lastDaily = status.daily_job?.last_run;
  const lastMonthly = status.monthly_job?.last_run;

  return (
    <Card className="border-emerald-200 bg-emerald-50/50">
      <CardContent className="pt-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-sm font-semibold text-emerald-800 mb-1">
              ⏰ Daily Auto-Send
            </div>
            <div className="text-xs text-slate-600 mb-2">{status.daily_job?.schedule}</div>
            {lastDaily ? (
              <div className="text-xs space-y-0.5">
                <div>Ultima esecuzione: <strong>{fmtDate(lastDaily.completed_at || lastDaily.started_at || lastDaily.created_at)}</strong></div>
                <div>
                  <span className="text-emerald-700">{lastDaily.records_sent || 0} record</span>
                  {' · '}
                  <span className="text-rose-700">{lastDaily.errors_count || 0} errori</span>
                  {' · '}
                  Status: <strong>{lastDaily.status}</strong>
                </div>
              </div>
            ) : (
              <div className="text-xs text-slate-500">Nessuna esecuzione automatica ancora.</div>
            )}
            <Button
              size="sm"
              variant="outline"
              className="mt-2 text-xs"
              onClick={triggerNow}
              disabled={triggering}
              data-testid="trigger-daily-now-btn"
            >
              {triggering ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : '🚀'} Esegui ora (test)
            </Button>
          </div>
          <div>
            <div className="text-sm font-semibold text-emerald-800 mb-1">
              📅 Monthly Recap (mese precedente)
            </div>
            <div className="text-xs text-slate-600 mb-2">{status.monthly_job?.schedule}</div>
            {lastMonthly ? (
              <div className="text-xs space-y-0.5">
                <div>Ultima esecuzione: <strong>{fmtDate(lastMonthly.completed_at || lastMonthly.created_at)}</strong></div>
                <div>
                  Mese: <strong>{lastMonthly.year}-{String(lastMonthly.month || 0).padStart(2, '0')}</strong>
                  {' · '}
                  <span className="text-emerald-700">{lastMonthly.records_sent || 0} record</span>
                  {' · '}
                  <span className="text-rose-700">{lastMonthly.errors_count || 0} errori</span>
                </div>
              </div>
            ) : (
              <div className="text-xs text-slate-500">Nessun recap eseguito ancora.</div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Coverage Report: per ogni immobile e mese mostra cosa è stato inviato e cosa manca.
 */
const IstatCoverageReport = ({ month, year }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterRegion, setFilterRegion] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [search, setSearch] = useState('');
  const [rectifying, setRectifying] = useState({}); // { [property_id]: bool }
  const [rectifyDate, setRectifyDate] = useState({}); // { [property_id]: 'YYYY-MM-DD' }

  const monthName = new Date(year, month - 1, 1).toLocaleDateString('it-IT', {
    month: 'long', year: 'numeric',
  });

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      const params = new URLSearchParams({ month: String(month), year: String(year) });
      if (filterRegion) params.append('regions', filterRegion);
      const res = await axios.get(`${API}/api/istat/coverage-report?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      toast.error('Errore caricamento report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [month, year, filterRegion]);

  const filteredRows = (data?.rows || []).filter((r) => {
    if (filterStatus && r.overall_status !== filterStatus) return false;
    if (search && !(r.property_name || '').toLowerCase().includes(search.toLowerCase()))
      return false;
    return true;
  });

  const exportCsv = () => {
    if (!data?.rows) return;
    const header = 'Immobile;Regione;Provincia;Stato;Arrivi;Partenze;Giorni tentati;Giorni inviati con record;Record totali;Errori;Codice struttura\n';
    const csv = header + data.rows.map(r => [
      (r.property_name||'').replace(/;/g,','),
      r.region||'', r.province||'',
      STATUS_LABELS[r.overall_status]?.text || r.overall_status,
      r.arrivals_in_month, r.departures_in_month,
      `${r.days_attempted}/${r.days_in_month}`,
      r.days_with_records,
      r.records_total_sent,
      r.days_failed,
      r.structure_code||''
    ].join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `coverage_${year}-${String(month).padStart(2,'0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const availableRegions = Array.from(
    new Set((data?.rows || []).map((r) => r.region).filter(Boolean))
  ).sort();

  const submitRectification = async (propertyId) => {
    const targetDate = rectifyDate[propertyId];
    if (!targetDate) {
      toast.error('Seleziona prima la data da rettificare');
      return;
    }
    if (!window.confirm(
      `Confermi l'invio della RETTIFICA per il ${targetDate}?\n\n` +
      `I record di quel giorno verranno re-inviati al portale ROSS1000 con i codici Polizia di Stato aggiornati. ` +
      `Pre-requisito: deve esistere un invio originale di SUCCESSO per quel giorno.`
    )) return;

    setRectifying(prev => ({ ...prev, [propertyId]: true }));
    try {
      const token = localStorage.getItem('hw_token');
      const res = await axios.post(
        `${API}/api/flussi-turistici/rettifica/${encodeURIComponent(propertyId)}?target_date=${targetDate}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success(`Rettifica inviata: ${res.data.records_submitted} record. ${res.data.message || ''}`);
      } else {
        toast.error(`Rettifica fallita: ${res.data.message || 'errore sconosciuto'}`, { duration: 8000 });
      }
      // Refresh report
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Errore rettifica', { duration: 8000 });
    } finally {
      setRectifying(prev => ({ ...prev, [propertyId]: false }));
    }
  };

  return (
    <div className="space-y-4" data-testid="istat-coverage-report">
      <SchedulerStatusCard month={month} year={year} />
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <CardTitle>📊 Stato Invio per Immobile — {monthName}</CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                Per ogni immobile vedi cosa è stato inviato al portale ROSS1000, cosa è in errore e
                cosa è ancora da fare.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Aggiorna
              </Button>
              <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data}>
                <Download className="h-4 w-4 mr-1" />
                CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary cards */}
          {data?.summary && (
            <div className="grid grid-cols-3 md:grid-cols-7 gap-2 mb-4 text-center text-xs">
              <div className="bg-emerald-50 p-2 rounded">
                <div className="text-emerald-700 font-bold text-lg">{data.summary.properties_ok}</div>
                <div>Inviati OK</div>
              </div>
              <div className="bg-amber-50 p-2 rounded">
                <div className="text-amber-700 font-bold text-lg">{data.summary.properties_partial}</div>
                <div>Parziali</div>
              </div>
              <div className="bg-rose-50 p-2 rounded">
                <div className="text-rose-700 font-bold text-lg">{data.summary.properties_all_failed}</div>
                <div>Tutti errori</div>
              </div>
              <div className="bg-blue-50 p-2 rounded">
                <div className="text-blue-700 font-bold text-lg">{data.summary.properties_pending}</div>
                <div>Da inviare</div>
              </div>
              <div className="bg-slate-100 p-2 rounded">
                <div className="text-slate-700 font-bold text-lg">{data.summary.properties_no_movements}</div>
                <div>Senza movim.</div>
              </div>
              <div className="bg-slate-100 p-2 rounded">
                <div className="text-slate-500 font-bold text-lg">{data.summary.properties_no_delegation}</div>
                <div>No delega</div>
              </div>
              <div className="bg-purple-50 p-2 rounded">
                <div className="text-purple-700 font-bold text-lg">{data.summary.total_records_sent}</div>
                <div>Record OK</div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-3">
            <select
              className="text-sm border rounded px-2 py-1"
              value={filterRegion}
              onChange={(e) => setFilterRegion(e.target.value)}
              data-testid="coverage-filter-region"
            >
              <option value="">Tutte le regioni</option>
              {availableRegions.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <select
              className="text-sm border rounded px-2 py-1"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              data-testid="coverage-filter-status"
            >
              <option value="">Tutti gli stati</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v.text}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="🔍 Cerca immobile..."
              className="text-sm border rounded px-2 py-1 flex-1 min-w-[200px]"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="coverage-search"
            />
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="border rounded overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs">
                  <tr>
                    <th className="text-left px-3 py-2"></th>
                    <th className="text-left px-3 py-2">Immobile</th>
                    <th className="text-left px-3 py-2">Stato</th>
                    <th className="text-right px-3 py-2">Arrivi</th>
                    <th className="text-right px-3 py-2">Partenze</th>
                    <th className="text-right px-3 py-2">Tentati</th>
                    <th className="text-right px-3 py-2">Inviati OK</th>
                    <th className="text-right px-3 py-2">Errori</th>
                    <th className="text-right px-3 py-2">Record OK</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r) => {
                    const isExp = expandedRow === r.property_id;
                    const lab = STATUS_LABELS[r.overall_status] || { text: r.overall_status, cls: 'bg-slate-100' };
                    return (
                      <React.Fragment key={r.property_id}>
                        <tr
                          className="border-t hover:bg-slate-50 cursor-pointer"
                          onClick={() => setExpandedRow(isExp ? null : r.property_id)}
                          data-testid={`coverage-row-${r.property_id}`}
                        >
                          <td className="px-3 py-2">
                            {isExp ? (
                              <ChevronDown className="h-4 w-4 text-slate-400" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-slate-400" />
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <div className="font-medium">{r.property_name}</div>
                            <div className="text-xs text-slate-500">
                              {r.region}{r.city ? ` · ${r.city}` : ''}{r.province ? ` (${r.province})` : ''}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant="outline" className={`${lab.cls} text-xs`}>
                              {lab.text}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-right">{r.arrivals_in_month}</td>
                          <td className="px-3 py-2 text-right">{r.departures_in_month}</td>
                          <td className="px-3 py-2 text-right text-xs text-slate-500">
                            {r.days_attempted}/{r.days_in_month}
                          </td>
                          <td className="px-3 py-2 text-right text-emerald-700 font-medium">
                            {r.days_with_records}
                          </td>
                          <td className="px-3 py-2 text-right text-rose-700 font-medium">
                            {r.days_failed > 0 ? r.days_failed : ''}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold">
                            {r.records_total_sent}
                          </td>
                        </tr>
                        {isExp && (
                          <tr className="bg-slate-50 border-t" data-testid={`coverage-detail-${r.property_id}`}>
                            <td colSpan={9} className="px-6 py-3 text-xs">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div>
                                  <strong>Codice struttura:</strong>
                                  <br />
                                  {r.structure_code || '—'}
                                </div>
                                <div>
                                  <strong>Modalità credenziali:</strong>
                                  <br />
                                  {r.credentials_mode || '—'}
                                </div>
                                <div>
                                  <strong>Delega attiva:</strong>
                                  <br />
                                  {r.has_delegation ? '✅ Sì' : '❌ No'}
                                </div>
                                <div>
                                  <strong>Mai tentati:</strong>
                                  <br />
                                  {r.days_never_attempted}/{r.days_in_month} giorni
                                </div>
                              </div>
                              {r.errors_summary && r.errors_summary.length > 0 && (
                                <div className="mt-3">
                                  <strong className="text-rose-700">Errori riscontrati:</strong>
                                  <ul className="list-disc ml-6 mt-1 space-y-1">
                                    {r.errors_summary.map((e, i) => (
                                      <li key={i} className="text-rose-600">
                                        <code className="bg-rose-50 px-1 rounded">×{e.count}</code>{' '}
                                        {e.error}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {r.days_empty_success > 0 && (
                                <div className="mt-2 text-amber-700">
                                  ⚠️ {r.days_empty_success} giorni inviati con 0 record (giornate vuote o anomalie)
                                </div>
                              )}

                              {/* RETTIFICHE: visibile solo se ci sono giorni gia' inviati con successo */}
                              {r.days_with_records > 0 && (
                                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Wrench className="h-4 w-4 text-blue-700" />
                                    <strong className="text-blue-800">Rettifica giorno gia' inviato</strong>
                                  </div>
                                  <p className="text-blue-700 mb-2">
                                    Re-invia un giorno (gia' spedito con successo) come <strong>rettifica</strong> al portale,
                                    aggiornando residenza/cittadinanza con i codici Polizia di Stato corretti.
                                  </p>
                                  <div className="flex flex-wrap items-center gap-2">
                                    <input
                                      type="date"
                                      value={rectifyDate[r.property_id] || ''}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        setRectifyDate(prev => ({ ...prev, [r.property_id]: e.target.value }));
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      className="text-sm border rounded px-2 py-1"
                                      min={`${year}-${String(month).padStart(2, '0')}-01`}
                                      max={`${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}`}
                                      data-testid={`rectify-date-${r.property_id}`}
                                    />
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-xs border-blue-400 text-blue-800 hover:bg-blue-100"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        submitRectification(r.property_id);
                                      }}
                                      disabled={!rectifyDate[r.property_id] || rectifying[r.property_id]}
                                      data-testid={`rectify-btn-${r.property_id}`}
                                    >
                                      {rectifying[r.property_id] ? (
                                        <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Invio...</>
                                      ) : (
                                        <><Wrench className="h-3 w-3 mr-1" /> Invia Rettifica</>
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {filteredRows.length === 0 && (
                    <tr>
                      <td colSpan={9} className="text-center py-6 text-slate-400">
                        Nessun immobile soddisfa i filtri
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {data && (
            <div className="mt-3 text-xs text-slate-500">
              Visualizzati {filteredRows.length} / {data.rows.length} immobili
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default IstatCoverageReport;
